import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { IconComponent } from '../../design-system/components/icon/icon.component';
import { PetszelSharedModule } from '../../petszel-shared.module';
import { emailsMatch } from './../email-match.validator';

export type EmailEditMode = 'add' | 'edit';

@Component({
  selector: 'lib-email-edit',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent, PetszelSharedModule],
  templateUrl: './email-edit.component.html',
  styleUrl: './email-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailEditComponent {
  private readonly formBuilder = inject(NonNullableFormBuilder);

  readonly mode = input<EmailEditMode>('add', {
    alias: 'mode',
  });

  readonly isSaving = input<boolean>(false, {
    alias: 'isSaving',
  });

  readonly errorMessage = input<string | null>(null, {
    alias: 'errorMessage',
  });

  readonly close = output<void>({
    alias: 'close',
  });

  readonly submitEmail = output<string>({
    alias: 'submitEmail',
  });

  readonly dismissError = output<void>({
    alias: 'dismissError',
  });

  readonly emailForm = this.formBuilder.group(
    {
      email: this.formBuilder.control('', {
        validators: [Validators.required, Validators.email],
      }),
      confirm: this.formBuilder.control('', {
        validators: [Validators.required, Validators.email],
      }),
    },
    {
      validators: [emailsMatch],
    },
  );

  get emailControl() {
    return this.emailForm.controls.email;
  }

  get confirmControl() {
    return this.emailForm.controls.confirm;
  }

  get emailValidationMessage(): string {
    if (this.emailControl.hasError('required')) {
      return 'Email is required.';
    }

    if (this.emailControl.hasError('email')) {
      return 'Enter a valid email address.';
    }

    return '';
  }

  get confirmValidationMessage(): string {
    if (this.confirmControl.hasError('required')) {
      return 'Please confirm the email address.';
    }

    if (this.confirmControl.hasError('email')) {
      return 'Enter a valid email address.';
    }

    if (this.emailForm.hasError('emailsDontMatch')) {
      return 'Emails do not match.';
    }

    return '';
  }

  onClose(): void {
    if (this.isSaving()) {
      return;
    }

    this.close.emit();
  }

  onDismissError(): void {
    this.dismissError.emit();
  }

  onSubmit(): void {
    if (this.isSaving() || this.emailForm.invalid) {
      this.emailForm.markAllAsTouched();
      return;
    }

    const email = this.emailControl.getRawValue().trim();

    if (!email) {
      this.emailControl.setErrors({
        required: true,
      });

      this.emailControl.markAsTouched();
      return;
    }

    this.submitEmail.emit(email);
  }
}
